<template>
  <DeleteContact
    :confirm-message="$t('Delete candidate')"
    :confirm-button-text="$t('Delete')"
    :on-confirm="runDeleteMutation"
    :show="show"
    :error-banner-message="errorBannerMessage"
    @cancel="onCancel"
  >
    <template #body>
      <div class="tw-mb-8">
        {{
          $t('Are you sure you want to delete :contact?', {
            contact: replace(
              '  ',
              ' ',
              `${contactName.firstName} ${contactName.middleName} ${contactName.lastName}`,
            ),
          }) +
          ' ' +
          $t(
            'All candidate information will be deleted permanently and this action cannot be undone.',
          )
        }}
      </div>
    </template>
  </DeleteContact>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable'
import { ref } from 'vue'
import { replace } from 'lodash'
import enums from '@/utils/enum'
import DeleteContact from '@/components/Contacts/DeleteContact.vue'
import { DeleteRecruiterCandidateMutation } from '@/components/Contacts/RecruiterFreelancer/graphql/mutations'
import { NetworkError } from '@/utils/notification'
import { FormValidation } from '@/utils/formValidation'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { Name } from '@/components/Contacts/types'

const { mutate: mutateDeleteCandidate } = useMutation(DeleteRecruiterCandidateMutation, {
  clientId: 'publicApi',
  refetchQueries: ['recruiterCandidates'],
})

const props = defineProps<{
  id: string
  contactName: Name
  show?: boolean
}>()

const errorBannerMessage = ref('')

const emit = defineEmits(['deleting', 'deleted', 'cancel'])

async function runDeleteMutation() {
  errorBannerMessage.value = ''
  emit('deleting')
  await mutateDeleteCandidate({
    input: {
      id: props.id,
    },
  })
    .then(() => {
      emit('deleted')
    })
    .catch((error) => {
      const { graphQLErrors } = error

      if (FormValidation.containsFormValidations(graphQLErrors)) {
        const errors = FormValidation.getSingularGraphqlErrors(graphQLErrors, 'input.')

        // eslint-disable-next-line -- Baseline eslint ignore please fix if working on this
        errorBannerMessage.value = errors['localId']
      } else {
        new NetworkError({
          severity: enums.notification.severity.LOW,
        }).dispatch()

        throw error
      }
    })
}
function onCancel() {
  errorBannerMessage.value = ''
  emit('cancel')
}
</script>
